import { Injectable, inject, computed, signal } from '@angular/core';
import { SignalRService, SignalRMethods } from '.';
import { LoggingService } from '../shared/logging/logging.service';
import { Subject } from 'rxjs';
import { ConfigService } from '../config/config.service';

/**
 * Interface for scorecard activity payload from server
 */
export interface ScorecardActivityPayload {
    type: 'ScorecardUpdated' | 'ScorecardCreated' | 'ScorecardMarkedOfficial' | 'ScorecardMarkedNotOfficial';
    scorecardId: string;
    roundScore?: number;
    finishScore?: number;
    official?: boolean;
    officialOnUtc?: string; // ISO 8601 date string from server
    occurredOnUtc: string; // ISO 8601 date string from server
    tournamentId: string;
    roundId: string;
    playerId: string;
    submittedBy?: string;
}

/**
 * Interface for scorecard activity data emitted to subscribers
 */
export interface ScorecardActivityData {
    type: 'ScorecardUpdated' | 'ScorecardCreated' | 'ScorecardMarkedOfficial' | 'ScorecardMarkedNotOfficial';
    scorecardId: string;
    timestamp: Date; // Uses server timestamp if available, otherwise current client time
    roundScore?: number;
    finishScore?: number;
    official?: boolean;
    officialOnUtc?: Date; // Converted from server timestamp
    tournamentId: string;
    roundId: string;
    playerId: string;
    submittedBy?: string;
}

/**
 * Interface for incoming scorecard event data from SignalR hub
 */
export interface ScorecardEventData {
    scorecardId: string;
    roundScore?: number;
    finishScore?: number;
    occurredOnUtc: string; // ISO 8601 date string from server
    tournamentId: string;
    roundId: string;
    playerId: string;
    submittedBy?: string;
}

/**
 * Interface for tournament active status payload from server
 */
export interface TournamentActiveStatusPayload {
    type: 'TournamentMarkedActive' | 'TournamentMarkedNotActive';
    tournamentId: string;
    active: boolean;
    activeOnUtc?: string; // ISO 8601 date string from server
    occurredOnUtc: string; // ISO 8601 date string from server
}

/**
 * Interface for tournament activity data emitted to subscribers
 */
export interface TournamentActivityData {
    type: 'TournamentMarkedActive' | 'TournamentMarkedNotActive';
    tournamentId: string;
    active: boolean;
    activeOnUtc?: Date; // Converted from server timestamp
    timestamp: Date; // Uses server timestamp if available, otherwise current client time
}

/**
 * Interface for incoming tournament event data from SignalR hub
 */
export interface TournamentActiveStatusEventData {
    tournamentId: string;
    active: boolean;
    activeOnUtc?: string; // ISO 8601 date string from server
    occurredOnUtc: string; // ISO 8601 date string from server
}

/**
 * Interface for round active status payload from server
 */
export interface RoundActiveStatusPayload {
    type: 'RoundMarkedActive' | 'RoundMarkedNotActive';
    roundId: string;
    active: boolean;
    activeOnUtc?: string; // ISO 8601 date string from server
    occurredOnUtc: string; // ISO 8601 date string from server
}

/**
 * Interface for round official status payload from server
 */
export interface RoundOfficialStatusPayload {
    type: 'RoundMarkedOfficial' | 'RoundMarkedNotOfficial';
    roundId: string;
    official: boolean;
    officialOnUtc?: string; // ISO 8601 date string from server
    occurredOnUtc: string; // ISO 8601 date string from server
}

/**
 * Interface for round activity data emitted to subscribers
 */
export interface RoundActivityData {
    type: 'RoundMarkedActive' | 'RoundMarkedNotActive';
    roundId: string;
    active: boolean;
    activeOnUtc?: Date; // Converted from server timestamp
    timestamp: Date; // Uses server timestamp if available, otherwise current client time
}

/**
 * Interface for round official activity data emitted to subscribers
 */
export interface RoundOfficialActivityData {
    type: 'RoundMarkedOfficial' | 'RoundMarkedNotOfficial';
    roundId: string;
    official: boolean;
    officialOnUtc?: Date; // Converted from server timestamp
    timestamp: Date; // Uses server timestamp if available, otherwise current client time
}

/**
 * Interface for incoming round event data from SignalR hub
 */
export interface RoundActiveStatusEventData {
    roundId: string;
    active: boolean;
    activeOnUtc?: string; // ISO 8601 date string from server
    occurredOnUtc: string; // ISO 8601 date string from server
}

/**
 * Interface for incoming round official status event data from SignalR hub
 */
export interface RoundOfficialStatusEventData {
    roundId: string;
    official: boolean;
    officialOnUtc?: string; // ISO 8601 date string from server
    occurredOnUtc: string; // ISO 8601 date string from server
}

/**
 * Interface for incoming scorecard official status event data from SignalR hub
 */
export interface ScorecardOfficialStatusEventData {
    scorecardId: string;
    official: boolean;
    officialOnUtc?: string; // ISO 8601 date string from server
    occurredOnUtc: string; // ISO 8601 date string from server
    tournamentId: string;
    roundId: string;
    playerId: string;
}


@Injectable({
    providedIn: 'root'
})
export class ScoringRealtimeService {
    private readonly signalRService = inject(SignalRService);
    private readonly configService = inject(ConfigService);
    private readonly loggingService = inject(LoggingService);
    private _isInitialized = false;
    private _isListening = false;

    // Add tracking for local operations with timestamps
    private readonly localOperations = new Map<string, Date>();
    private suppressLocalOperations = true;
    private suppressionWindowMs = 2000; // Default 2 seconds
    private suppressibleOperations: string[] = [];

    // Reactive stream for scorecard activity
    private readonly scorecardActivity$ = new Subject<ScorecardActivityData>();

    // Public observable for scorecard activity
    readonly scorecardActivity = this.scorecardActivity$.asObservable();

    // Reactive stream for tournament activity
    private readonly tournamentActivity$ = new Subject<TournamentActivityData>();

    // Public observable for tournament activity
    readonly tournamentActivity = this.tournamentActivity$.asObservable();

    // Reactive stream for round activity
    private readonly roundActivity$ = new Subject<RoundActivityData>();

    // Public observable for round activity
    readonly roundActivity = this.roundActivity$.asObservable();

    // Reactive stream for round official activity
    private readonly roundOfficialActivity$ = new Subject<RoundOfficialActivityData>();

    // Public observable for round official activity
    readonly roundOfficialActivity = this.roundOfficialActivity$.asObservable();

    // Persistent event storage (survives component lifecycle)
    private readonly _eventHistory = signal<ScorecardActivityData[]>([]);
    private readonly _totalEventsReceived = signal<number>(0);
    private readonly _lastEventReceived = signal<Date | null>(null);
    private readonly _isListeningSignal = signal<boolean>(false);

    // Public signals for component access
    readonly eventHistory = this._eventHistory.asReadonly();
    readonly totalEventsReceived = this._totalEventsReceived.asReadonly();
    readonly lastEventReceived = this._lastEventReceived.asReadonly();

    // Connection state signals (exposed as computed properties)
    readonly connectionState = this.signalRService.connectionState;
    readonly connectionError = this.signalRService.connectionError;

    // Computed signals for convenience
    readonly isConnected = computed(() => this.signalRService.isConnected());
    readonly isInitialized = computed(() => this._isInitialized);
    readonly hasError = computed(() => this.connectionError() !== null);
    readonly eventCount = computed(() => this._eventHistory().length);
    readonly transportType = computed(() => this.getFriendlyTransportName());
    readonly transportOptimality = computed(() => this.getTransportOptimality());
    readonly isListeningComputed = computed(() => this._isListeningSignal() && this.isConnected());

    /**
     * Initialize the real-time scoring service with anonymous access
     * @returns Promise that resolves when service is initialized and connected
     */
    async initializeWithEnvironment(): Promise<void> {
        if (this._isInitialized) {
            return;
        }

        // Load notification suppression config
        this.loadSuppressionConfig();

        // Initialize SignalR connection for anonymous access
        this.loggingService.debug('ScoringRealtimeService', 'Initializing with anonymous access');
        this.signalRService.initializeWithEnvironment();

        // Debug: Check what transport is being used after initialization
        this.loggingService.debug('ScoringRealtimeService', 'Transport info after initialization', this.getTransportInfo());

        // Register event handlers for golf scoring events
        this.setupEventHandlers();

        // Connect to the hub
        try {
            await this.signalRService.connect();
            this._isInitialized = true;
        } catch (error) {
            this.loggingService.error('ScoringRealtimeService', 'SignalR connection failed', error);
            throw error;
        }
    }

    /**
     * Initialize the real-time scoring service with authentication
     * @param authTokenFactory Function that returns authentication token
     * @returns Promise that resolves when service is initialized and connected
     */
    async initialize(authTokenFactory: () => string | Promise<string>): Promise<void> {
        if (this._isInitialized) {
            return;
        }

        // Load notification suppression config
        this.loadSuppressionConfig();

        // Initialize SignalR connection using config-based transport selection
        // The SignalR service will read transport preferences from config files
        this.loggingService.debug('ScoringRealtimeService', 'Initializing with config-based transport selection');
        this.signalRService.initializeForGolfScoring(authTokenFactory);

        // Debug: Check what transport is being used after initialization
        this.loggingService.debug('ScoringRealtimeService', 'Transport info after initialization', this.getTransportInfo());

        // Register event handlers for golf scoring events
        this.setupEventHandlers();

        // Connect to the hub
        try {
            await this.signalRService.connect();
            this._isInitialized = true;
        } catch (error) {
            this.loggingService.error('ScoringRealtimeService', 'SignalR connection failed', error);
            throw error;
        }
    }

    /**
     * Load notification suppression configuration
     */
    private loadSuppressionConfig(): void {
        const config = this.configService.config;

        if (config?.notifications) {
            // Load suppression settings
            this.suppressLocalOperations = config.notifications.suppressLocalOperations ?? true;
            this.suppressionWindowMs = config.notifications.suppressionWindowMs ?? 2000;
            this.suppressibleOperations = config.notifications.suppressibleOperations ?? [
                'ScorecardUpdated',
                'ScorecardMarkedOfficial',
                'ScorecardMarkedNotOfficial',
                'RoundMarkedActive',
                'RoundMarkedNotActive'
            ];

            this.loggingService.debug('ScoringRealtimeService', 'Notification suppression config loaded', {
                suppressLocalOperations: this.suppressLocalOperations,
                suppressionWindowMs: this.suppressionWindowMs,
                suppressibleOperations: this.suppressibleOperations
            });
        }
    }

    /**
     * Register a scorecard as being actively edited locally
     * This will suppress notifications for this scorecard for the duration of the suppression window
     * @param scorecardId The ID of the scorecard being edited
     */
    registerLocalScorecard(scorecardId: string): void {
        if (!scorecardId || !this.suppressLocalOperations) return;

        this.loggingService.debug('ScoringRealtimeService', `Registering local scorecard operation: ${scorecardId}`);
        this.localOperations.set(scorecardId, new Date());

        // Schedule cleanup after the suppression window to prevent memory leaks
        setTimeout(() => {
            this.cleanupExpiredLocalOperations();
        }, this.suppressionWindowMs + 1000); // Add 1 second buffer
    }

    /**
     * Clean up expired local operations that are older than the suppression window
     */
    private cleanupExpiredLocalOperations(): void {
        const now = new Date();
        let cleaned = 0;

        for (const [scorecardId, timestamp] of this.localOperations.entries()) {
            const age = now.getTime() - timestamp.getTime();
            if (age > this.suppressionWindowMs) {
                this.localOperations.delete(scorecardId);
                cleaned++;
            }
        }

        if (cleaned > 0) {
            this.loggingService.debug('ScoringRealtimeService', `Cleaned up ${cleaned} expired local scorecard operations`);
        }
    }

    /**
     * Check if an operation should be suppressed (local operation within suppression window)
     * @param type Operation type
     * @param scorecardId Scorecard ID
     */
    private shouldSuppressOperation(type: string, scorecardId: string): boolean {
        if (!this.suppressLocalOperations || !scorecardId) {
            return false;
        }

        // Check if this operation type is suppressible
        if (!this.suppressibleOperations.includes(type)) {
            return false;
        }

        // Check if this scorecard is registered as a local operation
        const timestamp = this.localOperations.get(scorecardId);
        if (!timestamp) {
            return false;
        }

        // Check if the operation is within the suppression window
        const now = new Date();
        const age = now.getTime() - timestamp.getTime();
        const shouldSuppress = age <= this.suppressionWindowMs;

        if (shouldSuppress) {
            this.loggingService.debug('ScoringRealtimeService', `Suppressing local ${type} notification for scorecard ${scorecardId} (age: ${age}ms)`);
        }

        return shouldSuppress;
    }

    /**
     * Get connection state information
     * @returns Computed signal containing connection state details
     */
    getConnectionInfo() {
        return this.signalRService.connectionInfo;
    }

    /**
     * Get the actual SignalR connection ID
     */
    getActualConnectionId(): string | null {
        return this.signalRService.getConnectionId();
    }

    /**
     * Check if connected to SignalR hub (legacy getter for backwards compatibility)
     * @deprecated Use isConnected() signal instead
     */
    get isConnectedLegacy() {
        return this.signalRService.isConnected;
    }

    /**
     * Disconnect from the SignalR hub and clean up resources
     * @returns Promise that resolves when disconnection is complete
     */
    async disconnect(): Promise<void> {
        try {
            // Clean up event handlers first
            this.cleanupEventHandlers();

            await this.signalRService.disconnect();
            this._isInitialized = false;

            // Don't complete the Subject - just clear any pending events
            // This allows the service to be reinitialized later
            this.loggingService.info('ScoringRealtimeService', 'ScoringRealtimeService disconnected - ready for reinitialization');
        } catch (error) {
            this.loggingService.error('ScoringRealtimeService', 'Error during disconnect', error);
            throw error;
        }
    }

    /**
     * Refresh the connection to update authentication state
     * This is useful when authentication changes (login/logout)
     */
    async refreshConnection(): Promise<void> {
        if (!this._isInitialized) {
            this.loggingService.warn('ScoringRealtimeService', 'ScoringRealtimeService not initialized - cannot refresh connection');
            return;
        }

        try {
            this.loggingService.info('ScoringRealtimeService', 'Refreshing SignalR connection for authentication update...');

            // Clean up event handlers first
            this.cleanupEventHandlers();

            // Full disconnect and reset initialization state
            await this.signalRService.disconnect();
            this._isInitialized = false;

            this.loggingService.info('ScoringRealtimeService', 'SignalR connection refreshed successfully');
        } catch (error) {
            this.loggingService.error('ScoringRealtimeService', 'Error refreshing SignalR connection', error);
            throw error;
        }
    }

    /**
     * Reinitialize the service with a fresh token factory
     * This forces a complete reinitialization including new authentication context
     */
    async reinitialize(authTokenFactory: () => string | Promise<string>): Promise<void> {
        try {
            this.loggingService.info('ScoringRealtimeService', 'Reinitializing ScoringRealtimeService with fresh authentication...');

            // Reset initialization state to allow reinitialization
            this._isInitialized = false;

            // Now initialize with the fresh token factory
            await this.initialize(authTokenFactory);

            this.loggingService.info('ScoringRealtimeService', 'ScoringRealtimeService reinitialized successfully');
        } catch (error) {
            this.loggingService.error('ScoringRealtimeService', 'Error reinitializing ScoringRealtimeService', error);
            throw error;
        }
    }

    /**
     * Get information about the current transport being used
     */
    getTransportInfo(): { transport: string | null; isOptimal: boolean; isEfficient: boolean } {
        const transport = this.signalRService.getCurrentTransport();
        const isOptimal = this.signalRService.isUsingWebSockets();
        const isEfficient = this.signalRService.isUsingServerSentEvents();

        return { transport, isOptimal, isEfficient };
    }

    /**
     * Check if connection is using optimal WebSocket transport
     */
    isUsingOptimalTransport(): boolean {
        return this.signalRService.isUsingWebSockets();
    }

    /**
     * Check if connection is using efficient Server-Sent Events transport
     */
    isUsingEfficientTransport(): boolean {
        return this.signalRService.isUsingServerSentEvents();
    }

    /**
     * Test if WebSockets are working and optionally upgrade connection
     */
    async testAndEnableWebSockets(): Promise<boolean> {
        const testResult = await this.signalRService.testWebSocketConnection();

        if (testResult.success) {
            this.loggingService.info('ScoringRealtimeService', 'WebSocket test successful - WebSockets are available');
            return true;
        } else {
            this.loggingService.warn('ScoringRealtimeService', 'WebSocket test failed: ' + testResult.error);
            this.loggingService.warn('ScoringRealtimeService', 'Continuing with current reliable transport');
            return false;
        }
    }

    /**
     * Start listening to events (sets up event handlers)
     */
    async startListening(): Promise<void> {
        if (!this.isConnected()) {
            throw new Error('Cannot start listening - not connected to SignalR hub');
        }

        if (this._isListening) {
            this.loggingService.debug('ScoringRealtimeService', 'Already listening to SignalR events');
            return;
        }

        this.setupEventHandlers();
        this._isListening = true;
        this._isListeningSignal.set(true);
        this.loggingService.info('ScoringRealtimeService', 'Started listening to SignalR events');
    }

    /**
     * Stop listening to events (removes event handlers)
     */
    stopListening(): void {
        if (!this._isListening) {
            this.loggingService.debug('ScoringRealtimeService', 'Not currently listening to SignalR events');
            return;
        }

        this.cleanupEventHandlers();
        this._isListening = false;
        this._isListeningSignal.set(false);
        this.loggingService.info('ScoringRealtimeService', 'Stopped listening to SignalR events');
    }

    /**
     * Check if currently listening to events
     */
    isListening(): boolean {
        return this._isListening && this.isConnected();
    }

    /**
     * Get user-friendly transport name with description
     */
    getFriendlyTransportName(): string {
        if (!this.isConnected()) {
            return 'Not Connected';
        }

        const currentTransport = this.signalRService.getCurrentTransport();

        if (!currentTransport) {
            return 'Connected (Transport Unknown)';
        }

        switch (currentTransport) {
            case 'WebSocketsTransport':
            case 'WebSocketTransport':
                return 'WebSockets (Optimal)';
            case 'ServerSentEventsTransport':
                return 'Server-Sent Events (Efficient)';
            case 'LongPollingTransport':
                return 'Long Polling (Fallback)';
            default:
                return `${currentTransport} (Unknown)`;
        }
    }

    /**
     * Get transport optimality information
     */
    getTransportOptimality(): { level: 'optimal' | 'efficient' | 'fallback'; description: string; color: string } {
        if (!this.isConnected()) {
            return { level: 'fallback', description: 'Not connected', color: 'text-muted' };
        }

        const currentTransport = this.signalRService.getCurrentTransport();

        if (!currentTransport) {
            return {
                level: 'fallback',
                description: 'Connected but transport type unknown',
                color: 'text-warning'
            };
        }

        switch (currentTransport) {
            case 'WebSocketsTransport':
            case 'WebSocketTransport':
                return {
                    level: 'optimal',
                    description: 'Best performance with bi-directional communication',
                    color: 'text-success'
                };
            case 'ServerSentEventsTransport':
                return {
                    level: 'efficient',
                    description: 'Good performance with server-to-client streaming',
                    color: 'text-info'
                };
            case 'LongPollingTransport':
                return {
                    level: 'fallback',
                    description: 'Basic compatibility mode with higher latency',
                    color: 'text-warning'
                };
            default:
                return {
                    level: 'fallback',
                    description: `Unknown transport type: ${currentTransport}`,
                    color: 'text-muted'
                };
        }
    }

    /**
     * Attempt to upgrade to a better transport
     */
    async attemptTransportUpgrade(): Promise<{ success: boolean; message: string; newTransport?: string }> {
        const currentTransport = this.signalRService.getCurrentTransport();

        if (!this.isConnected()) {
            return { success: false, message: 'Cannot upgrade transport - not connected' };
        }

        // If already using WebSockets, no upgrade needed
        if (currentTransport === 'WebSocketsTransport' || currentTransport === 'WebSocketTransport') {
            return { success: false, message: 'Already using optimal transport (WebSockets)' };
        }

        try {
            // Test WebSocket connection
            const testResult = await this.signalRService.testWebSocketConnection();

            if (testResult.success) {
                // If WebSockets work, we would need to reconnect with WebSockets preferred
                // For now, we'll just return success and let the user know they need to reconnect
                return {
                    success: true,
                    message: 'WebSockets are available. Reconnect to upgrade to optimal transport.',
                    newTransport: 'WebSocketsTransport'
                };
            } else {
                return {
                    success: false,
                    message: `WebSockets not available: ${testResult.error}. Current transport is the best available.`
                };
            }
        } catch (error) {
            return {
                success: false,
                message: `Transport upgrade failed: ${error}`
            };
        }
    }

    /**
     * Force reconnection with preferred transport
     */
    async forceTransportReconnection(): Promise<void> {
        if (!this._isInitialized) {
            throw new Error('Service not initialized');
        }

        this.loggingService.info('ScoringRealtimeService', 'Forcing reconnection to attempt transport upgrade...');

        // Store current auth token factory (we'll need to get this from the current connection)
        // For now, we'll just disconnect and let the user reinitialize
        await this.disconnect();

        this.loggingService.info('ScoringRealtimeService', 'Disconnected. User will need to reinitialize connection.');
    }

    /**
     * Clear all stored events
     */
    clearEventHistory(): void {
        this._eventHistory.set([]);
        this._totalEventsReceived.set(0);
        this._lastEventReceived.set(null);
        this.loggingService.debug('ScoringRealtimeService', 'Event history cleared');
    }

    /**
     * Get detailed connection statistics
     */
    getConnectionStats() {
        return this.signalRService.getConnectionStats();
    }

    /**
     * Force immediate reconnection attempt
     */
    async forceReconnect(): Promise<void> {
        if (!this._isInitialized) {
            throw new Error('Service not initialized');
        }

        this.loggingService.info('ScoringRealtimeService', 'Forcing reconnection...');
        await this.signalRService.forceReconnect();

        // Re-setup event handlers after reconnection
        setTimeout(() => {
            this.setupEventHandlers();
        }, 100);
    }

    /**
     * Check if connection is healthy
     */
    isConnectionHealthy(): boolean {
        const stats = this.getConnectionStats();
        return stats.isHealthy;
    }

    /**
     * Get connection health status
     */
    getHealthStatus(): 'healthy' | 'unhealthy' | 'unknown' {
        const stats = this.getConnectionStats();
        return stats.healthStatus as 'healthy' | 'unhealthy' | 'unknown';
    }

    /**
     * Test method to manually trigger scorecard activity broadcast
     * This helps debug if the observable subscription is working
     */
    testBroadcast(): void {
        this.loggingService.debug('ScoringRealtimeService', 'Testing scorecard activity broadcast...');
        const testPayload: ScorecardActivityPayload = {
            type: 'ScorecardUpdated',
            scorecardId: 'test-scorecard-123',
            occurredOnUtc: new Date().toISOString(),
            tournamentId: 'test-tournament-123',
            roundId: 'test-round-123',
            playerId: 'test-player-123'
        };
        this.broadcastScorecardActivity(testPayload);
    }

    /**
     * Test method to manually trigger tournament activity broadcast
     */
    testTournamentBroadcast(): void {
        this.loggingService.warn('ScoringRealtimeService', 'testTournamentBroadcast service method called');
        this.loggingService.debug('ScoringRealtimeService', 'Testing tournament activity broadcast...');
        const testPayload: TournamentActiveStatusPayload = {
            type: 'TournamentMarkedActive',
            tournamentId: `test-tournament-${Date.now()}`,
            active: true,
            activeOnUtc: new Date().toISOString(),
            occurredOnUtc: new Date().toISOString()
        };
        this.loggingService.warn('ScoringRealtimeService', 'About to call broadcastTournamentActivity with:', testPayload);
        this.broadcastTournamentActivity(testPayload);
        this.loggingService.warn('ScoringRealtimeService', 'broadcastTournamentActivity called');
    }

    /**
     * Test method to manually trigger round activity broadcast
     */
    testRoundBroadcast(): void {
        this.loggingService.warn('ScoringRealtimeService', 'testRoundBroadcast service method called');
        this.loggingService.debug('ScoringRealtimeService', 'Testing round activity broadcast...');
        const testPayload: RoundActiveStatusPayload = {
            type: 'RoundMarkedActive',
            roundId: `test-round-${Date.now()}`,
            active: true,
            activeOnUtc: new Date().toISOString(),
            occurredOnUtc: new Date().toISOString()
        };
        this.loggingService.warn('ScoringRealtimeService', 'About to call broadcastRoundActivity with:', testPayload);
        this.broadcastRoundActivity(testPayload);
        this.loggingService.warn('ScoringRealtimeService', 'broadcastRoundActivity called');
    }

    /**
     * Clean up SignalR event handlers to prevent duplicate registrations
     */
    private cleanupEventHandlers(): void {
        try {
            // Remove all scorecard activity handlers
            this.signalRService.off(SignalRMethods.SCORECARD_UPDATED);
            this.signalRService.off(SignalRMethods.SCORECARD_CREATED);
            this.signalRService.off(SignalRMethods.SCORECARD_MARKED_OFFICIAL);
            this.signalRService.off(SignalRMethods.SCORECARD_MARKED_NOT_OFFICIAL);

            // Remove all tournament activity handlers
            this.signalRService.off(SignalRMethods.TOURNAMENT_MARKED_ACTIVE);
            this.signalRService.off(SignalRMethods.TOURNAMENT_MARKED_NOT_ACTIVE);

            // Remove all round activity handlers
            this.signalRService.off(SignalRMethods.ROUND_MARKED_ACTIVE);
            this.signalRService.off(SignalRMethods.ROUND_MARKED_NOT_ACTIVE);
            this.signalRService.off(SignalRMethods.ROUND_MARKED_OFFICIAL);
            this.signalRService.off(SignalRMethods.ROUND_MARKED_NOT_OFFICIAL);

            this._isListening = false;
            this._isListeningSignal.set(false);
            this.loggingService.debug('ScoringRealtimeService', 'Scoring realtime event handlers cleaned up');
        } catch (error) {
            this.loggingService.error('ScoringRealtimeService', 'Error cleaning up event handlers', error);
        }
    }

    private setupEventHandlers(): void {
        // Clean up any existing handlers first to prevent duplicates
        this.cleanupEventHandlers();

        this.loggingService.debug('ScoringRealtimeService', 'Setting up SignalR event handlers...');
        this.loggingService.debug('ScoringRealtimeService', 'Registering handlers for', [
            SignalRMethods.SCORECARD_UPDATED,
            SignalRMethods.SCORECARD_CREATED,
            SignalRMethods.SCORECARD_MARKED_OFFICIAL,
            SignalRMethods.SCORECARD_MARKED_NOT_OFFICIAL,
            SignalRMethods.TOURNAMENT_MARKED_ACTIVE,
            SignalRMethods.TOURNAMENT_MARKED_NOT_ACTIVE,
            SignalRMethods.ROUND_MARKED_ACTIVE,
            SignalRMethods.ROUND_MARKED_NOT_ACTIVE,
            SignalRMethods.ROUND_MARKED_OFFICIAL,
            SignalRMethods.ROUND_MARKED_NOT_OFFICIAL
        ]);

        // Handler for scorecard updated events
        this.signalRService.on(SignalRMethods.SCORECARD_UPDATED, (data: ScorecardEventData) => {
            try {
                this.loggingService.debug('ScoringRealtimeService', 'Scorecard updated event received', data);
                this.handleScorecardEvent('ScorecardUpdated', data);
            } catch (error) {
                this.loggingService.error('ScoringRealtimeService', 'Error handling scorecard updated event', error);
            }
        });

        // Handler for scorecard created events
        this.signalRService.on(SignalRMethods.SCORECARD_CREATED, (data: ScorecardEventData) => {
            try {
                this.loggingService.debug('ScoringRealtimeService', 'Scorecard created event received', data);
                this.handleScorecardEvent('ScorecardCreated', data);
            } catch (error) {
                this.loggingService.error('ScoringRealtimeService', 'Error handling scorecard created event', error);
            }
        });

        // Handler for scorecard marked official events
        this.signalRService.on(SignalRMethods.SCORECARD_MARKED_OFFICIAL, (data: ScorecardOfficialStatusEventData) => {
            try {
                this.loggingService.debug('ScoringRealtimeService', 'Scorecard marked official event received', data);
                this.handleScorecardOfficialStatusEvent('ScorecardMarkedOfficial', data);
            } catch (error) {
                this.loggingService.error('ScoringRealtimeService', 'Error handling scorecard marked official event', error);
            }
        });

        // Handler for scorecard marked not official events
        this.signalRService.on(SignalRMethods.SCORECARD_MARKED_NOT_OFFICIAL, (data: ScorecardOfficialStatusEventData) => {
            try {
                this.loggingService.debug('ScoringRealtimeService', 'Scorecard marked not official event received', data);
                this.handleScorecardOfficialStatusEvent('ScorecardMarkedNotOfficial', data);
            } catch (error) {
                this.loggingService.error('ScoringRealtimeService', 'Error handling scorecard marked not official event', error);
            }
        });

        // Handler for tournament marked active events
        this.signalRService.on(SignalRMethods.TOURNAMENT_MARKED_ACTIVE, (data: TournamentActiveStatusEventData) => {
            try {
                this.loggingService.debug('ScoringRealtimeService', 'Tournament marked active event received', data);
                this.handleTournamentActiveStatusEvent('TournamentMarkedActive', data);
            } catch (error) {
                this.loggingService.error('ScoringRealtimeService', 'Error handling tournament marked active event', error);
            }
        });

        // Handler for tournament marked not active events
        this.signalRService.on(SignalRMethods.TOURNAMENT_MARKED_NOT_ACTIVE, (data: TournamentActiveStatusEventData) => {
            try {
                this.loggingService.debug('ScoringRealtimeService', 'Tournament marked not active event received', data);
                this.handleTournamentActiveStatusEvent('TournamentMarkedNotActive', data);
            } catch (error) {
                this.loggingService.error('ScoringRealtimeService', 'Error handling tournament marked not active event', error);
            }
        });

        // Handler for round marked active events
        this.signalRService.on(SignalRMethods.ROUND_MARKED_ACTIVE, (data: RoundActiveStatusEventData) => {
            try {
                this.loggingService.debug('ScoringRealtimeService', 'Round marked active event received', data);
                this.handleRoundActiveStatusEvent('RoundMarkedActive', data);
            } catch (error) {
                this.loggingService.error('ScoringRealtimeService', 'Error handling round marked active event', error);
            }
        });

        // Handler for round marked not active events
        this.signalRService.on(SignalRMethods.ROUND_MARKED_NOT_ACTIVE, (data: RoundActiveStatusEventData) => {
            try {
                this.loggingService.debug('ScoringRealtimeService', 'Round marked not active event received', data);
                this.handleRoundActiveStatusEvent('RoundMarkedNotActive', data);
            } catch (error) {
                this.loggingService.error('ScoringRealtimeService', 'Error handling round marked not active event', error);
            }
        });

        // Handler for round marked official events
        this.signalRService.on(SignalRMethods.ROUND_MARKED_OFFICIAL, (data: RoundOfficialStatusEventData) => {
            try {
                this.loggingService.debug('ScoringRealtimeService', 'Round marked official event received', data);
                this.handleRoundOfficialStatusEvent('RoundMarkedOfficial', data);
            } catch (error) {
                this.loggingService.error('ScoringRealtimeService', 'Error handling round marked official event', error);
            }
        });

        // Handler for round marked not official events
        this.signalRService.on(SignalRMethods.ROUND_MARKED_NOT_OFFICIAL, (data: RoundOfficialStatusEventData) => {
            try {
                this.loggingService.debug('ScoringRealtimeService', 'Round marked not official event received', data);
                this.handleRoundOfficialStatusEvent('RoundMarkedNotOfficial', data);
            } catch (error) {
                this.loggingService.error('ScoringRealtimeService', 'Error handling round marked not official event', error);
            }
        });

        this._isListening = true;
        this._isListeningSignal.set(true);
        this.loggingService.debug('ScoringRealtimeService', 'Scoring realtime event handlers registered successfully');
        this.loggingService.debug('ScoringRealtimeService', 'Registered handlers for:', [
            SignalRMethods.SCORECARD_UPDATED,
            SignalRMethods.SCORECARD_CREATED,
            SignalRMethods.SCORECARD_MARKED_OFFICIAL,
            SignalRMethods.SCORECARD_MARKED_NOT_OFFICIAL,
            SignalRMethods.TOURNAMENT_MARKED_ACTIVE,
            SignalRMethods.TOURNAMENT_MARKED_NOT_ACTIVE,
            SignalRMethods.ROUND_MARKED_ACTIVE,
            SignalRMethods.ROUND_MARKED_NOT_ACTIVE
        ]);
        this.loggingService.debug('ScoringRealtimeService', 'Observable subject has observers:', this.scorecardActivity$.observers.length);
        this.loggingService.debug('ScoringRealtimeService', 'Tournament observable subject has observers:', this.tournamentActivity$.observers.length);
        this.loggingService.debug('ScoringRealtimeService', 'Round observable subject has observers:', this.roundActivity$.observers.length);
    }

    /**
     * Handle individual scorecard events from the server
     */
    private handleScorecardEvent(type: 'ScorecardUpdated' | 'ScorecardCreated', data: ScorecardEventData): void {
        try {
            this.loggingService.debug('ScoringRealtimeService', `Handling ${type} event:`, data);

            if (!data || !data.scorecardId) {
                this.loggingService.warn('ScoringRealtimeService', `${type} event missing scorecardId. Data:`, data);
                return;
            }

            // Convert server event data to our payload format
            const payload: ScorecardActivityPayload = {
                type,
                ...data,
            };

            this.loggingService.debug('ScoringRealtimeService', `Converted ${type} event to payload:`, payload);
            this.broadcastScorecardActivity(payload);
        } catch (error) {
            this.loggingService.error('ScoringRealtimeService', `Error handling ${type} event:`, error);
        }
    }

    /**
     * Handle scorecard official status events from the server
     */
    private handleScorecardOfficialStatusEvent(
        type: 'ScorecardMarkedOfficial' | 'ScorecardMarkedNotOfficial',
        data: ScorecardOfficialStatusEventData
    ): void {
        try {
            this.loggingService.debug('ScoringRealtimeService', `Handling ${type} event:`, data);

            if (!data || !data.scorecardId) {
                this.loggingService.warn('ScoringRealtimeService', `${type} event missing scorecardId. Data:`, data);
                return;
            }

            // Convert server event data to our payload format
            const payload: ScorecardActivityPayload = {
                type,
                scorecardId: data.scorecardId,
                official: data.official,
                officialOnUtc: data.officialOnUtc,
                occurredOnUtc: data.occurredOnUtc,
                tournamentId: data.tournamentId,
                roundId: data.roundId,
                playerId: data.playerId,
            };

            this.loggingService.debug('ScoringRealtimeService', `Converted ${type} event to payload:`, payload);
            this.broadcastScorecardActivity(payload);
        } catch (error) {
            this.loggingService.error('ScoringRealtimeService', `Error handling ${type} event:`, error);
        }
    }

    /**
     * Handle tournament active status events from the server
     */
    private handleTournamentActiveStatusEvent(
        type: 'TournamentMarkedActive' | 'TournamentMarkedNotActive',
        data: TournamentActiveStatusEventData
    ): void {
        try {
            this.loggingService.debug('ScoringRealtimeService', `Handling ${type} event:`, data);

            if (!data || !data.tournamentId) {
                this.loggingService.warn('ScoringRealtimeService', `${type} event missing tournamentId. Data:`, data);
                return;
            }

            // Convert server event data to our payload format
            const payload: TournamentActiveStatusPayload = {
                type,
                tournamentId: data.tournamentId,
                active: data.active,
                activeOnUtc: data.activeOnUtc,
                occurredOnUtc: data.occurredOnUtc,
            };

            this.loggingService.debug('ScoringRealtimeService', `Converted ${type} event to payload:`, payload);
            this.broadcastTournamentActivity(payload);
        } catch (error) {
            this.loggingService.error('ScoringRealtimeService', `Error handling ${type} event:`, error);
        }
    }

    /**
     * Handle round active status events from the server
     */
    private handleRoundActiveStatusEvent(
        type: 'RoundMarkedActive' | 'RoundMarkedNotActive',
        data: RoundActiveStatusEventData
    ): void {
        try {
            this.loggingService.debug('ScoringRealtimeService', `Handling ${type} event:`, data);

            if (!data || !data.roundId) {
                this.loggingService.warn('ScoringRealtimeService', `${type} event missing roundId. Data:`, data);
                return;
            }

            // Convert server event data to our payload format
            const payload: RoundActiveStatusPayload = {
                type,
                roundId: data.roundId,
                active: data.active,
                activeOnUtc: data.activeOnUtc,
                occurredOnUtc: data.occurredOnUtc,
            };

            this.loggingService.debug('ScoringRealtimeService', `Converted ${type} event to payload:`, payload);
            this.broadcastRoundActivity(payload);
        } catch (error) {
            this.loggingService.error('ScoringRealtimeService', `Error handling ${type} event:`, error);
        }
    }

    // This method emits events through reactive stream for components to subscribe to
    private broadcastScorecardActivity(payload: ScorecardActivityPayload): void {
        this.loggingService.debug('ScoringRealtimeService', 'broadcastScorecardActivity called with:', payload);

        try {
            if (!payload) {
                this.loggingService.warn('ScoringRealtimeService', 'Scorecard activity payload is null/undefined');
                return;
            }

            if (!payload.scorecardId) {
                this.loggingService.warn('ScoringRealtimeService', 'Scorecard activity payload missing ScorecardId. Payload:', payload);
                this.loggingService.warn('ScoringRealtimeService', 'Available properties:', Object.keys(payload));
                return;
            }

            // Check if this is a local operation that should be suppressed
            if (this.shouldSuppressOperation(payload.type, payload.scorecardId)) {
                this.loggingService.debug('ScoringRealtimeService', `Suppressing notification for local ${payload.type} on scorecard ${payload.scorecardId}`);
                return;
            }

            const activityData: ScorecardActivityData = {
                type: payload.type,
                scorecardId: payload.scorecardId,
                timestamp: new Date(payload.occurredOnUtc), // Use server timestamp
                roundScore: payload.roundScore,
                finishScore: payload.finishScore,
                official: payload.official,
                officialOnUtc: payload.officialOnUtc ? new Date(payload.officialOnUtc) : undefined,
                tournamentId: payload.tournamentId,
                roundId: payload.roundId,
                playerId: payload.playerId,
                submittedBy: payload.submittedBy,
            };

            // Store event in service-level history (persists across component lifecycle)
            this.storeEventInHistory(activityData);

            this.loggingService.debug('ScoringRealtimeService', 'Broadcasting scorecard activity to subscribers:', activityData);
            this.loggingService.debug('ScoringRealtimeService', 'Subject has observers:', this.scorecardActivity$.observers.length);
            this.loggingService.debug('ScoringRealtimeService', 'Subject is closed:', this.scorecardActivity$.closed);
            this.loggingService.debug('ScoringRealtimeService', 'About to call next() on Subject...');
            this.scorecardActivity$.next(activityData);
            this.loggingService.debug('ScoringRealtimeService', 'Scorecard activity broadcast sent to Subject successfully');
        } catch (error) {
            this.loggingService.error('ScoringRealtimeService', 'Error broadcasting scorecard activity:', error);
        }
    }

    // This method emits tournament events through reactive stream for components to subscribe to
    private broadcastTournamentActivity(payload: TournamentActiveStatusPayload): void {
        this.loggingService.debug('ScoringRealtimeService', 'broadcastTournamentActivity called with:', payload);

        try {
            if (!payload) {
                this.loggingService.warn('ScoringRealtimeService', 'Tournament activity payload is null/undefined');
                return;
            }

            if (!payload.tournamentId) {
                this.loggingService.warn('ScoringRealtimeService', 'Tournament activity payload missing tournamentId. Payload:', payload);
                this.loggingService.warn('ScoringRealtimeService', 'Available properties:', Object.keys(payload));
                return;
            }

            const activityData: TournamentActivityData = {
                type: payload.type,
                tournamentId: payload.tournamentId,
                active: payload.active,
                activeOnUtc: payload.activeOnUtc ? new Date(payload.activeOnUtc) : undefined,
                timestamp: new Date(payload.occurredOnUtc), // Use server timestamp
            };

            this.loggingService.debug('ScoringRealtimeService', 'Broadcasting tournament activity to subscribers:', activityData);
            this.loggingService.debug('ScoringRealtimeService', 'Tournament subject has observers:', this.tournamentActivity$.observers.length);
            this.loggingService.debug('ScoringRealtimeService', 'Tournament subject is closed:', this.tournamentActivity$.closed);
            this.loggingService.debug('ScoringRealtimeService', 'About to call next() on tournament Subject...');
            this.tournamentActivity$.next(activityData);
            this.loggingService.debug('ScoringRealtimeService', 'Tournament activity broadcast sent to Subject successfully');
        } catch (error) {
            this.loggingService.error('ScoringRealtimeService', 'Error broadcasting tournament activity:', error);
        }
    }

    // This method emits round events through reactive stream for components to subscribe to
    private broadcastRoundActivity(payload: RoundActiveStatusPayload): void {
        this.loggingService.debug('ScoringRealtimeService', 'broadcastRoundActivity called with:', payload);

        try {
            if (!payload) {
                this.loggingService.warn('ScoringRealtimeService', 'Round activity payload is null/undefined');
                return;
            }

            if (!payload.roundId) {
                this.loggingService.warn('ScoringRealtimeService', 'Round activity payload missing roundId. Payload:', payload);
                this.loggingService.warn('ScoringRealtimeService', 'Available properties:', Object.keys(payload));
                return;
            }

            const activityData: RoundActivityData = {
                type: payload.type,
                roundId: payload.roundId,
                active: payload.active,
                activeOnUtc: payload.activeOnUtc ? new Date(payload.activeOnUtc) : undefined,
                timestamp: new Date(payload.occurredOnUtc), // Use server timestamp
            };

            this.loggingService.debug('ScoringRealtimeService', 'Broadcasting round activity to subscribers:', activityData);
            this.loggingService.debug('ScoringRealtimeService', 'Round subject has observers:', this.roundActivity$.observers.length);
            this.loggingService.debug('ScoringRealtimeService', 'Round subject is closed:', this.roundActivity$.closed);
            this.loggingService.debug('ScoringRealtimeService', 'About to call next() on round Subject...');
            this.roundActivity$.next(activityData);
            this.loggingService.debug('ScoringRealtimeService', 'Round activity broadcast sent to Subject successfully');
        } catch (error) {
            this.loggingService.error('ScoringRealtimeService', 'Error broadcasting round activity:', error);
        }
    }

    /**
     * Handle round official status events from the server
     */
    private handleRoundOfficialStatusEvent(
        type: 'RoundMarkedOfficial' | 'RoundMarkedNotOfficial',
        data: RoundOfficialStatusEventData
    ): void {
        try {
            this.loggingService.debug('ScoringRealtimeService', `Handling ${type} event:`, data);

            if (!data || !data.roundId) {
                this.loggingService.warn('ScoringRealtimeService', `${type} event missing roundId. Data:`, data);
                return;
            }

            // Convert server event data to our payload format
            const payload: RoundOfficialStatusPayload = {
                type,
                roundId: data.roundId,
                official: data.official,
                officialOnUtc: data.officialOnUtc,
                occurredOnUtc: data.occurredOnUtc,
            };

            this.loggingService.debug('ScoringRealtimeService', `Converted ${type} event to payload:`, payload);
            this.broadcastRoundOfficialActivity(payload);
        } catch (error) {
            this.loggingService.error('ScoringRealtimeService', `Error handling ${type} event:`, error);
        }
    }

    // This method emits round official events through reactive stream for components to subscribe to
    private broadcastRoundOfficialActivity(payload: RoundOfficialStatusPayload): void {
        this.loggingService.debug('ScoringRealtimeService', 'broadcastRoundOfficialActivity called with:', payload);

        try {
            if (!payload) {
                this.loggingService.warn('ScoringRealtimeService', 'Round official activity payload is null/undefined');
                return;
            }

            if (!payload.roundId) {
                this.loggingService.warn('ScoringRealtimeService', 'Round official activity payload missing roundId. Payload:', payload);
                this.loggingService.warn('ScoringRealtimeService', 'Available properties:', Object.keys(payload));
                return;
            }

            const activityData: RoundOfficialActivityData = {
                type: payload.type,
                roundId: payload.roundId,
                official: payload.official,
                officialOnUtc: payload.officialOnUtc ? new Date(payload.officialOnUtc) : undefined,
                timestamp: new Date(payload.occurredOnUtc), // Use server timestamp
            };

            this.loggingService.debug('ScoringRealtimeService', 'Broadcasting round official activity to subscribers:', activityData);
            this.loggingService.debug('ScoringRealtimeService', 'Round official subject has observers:', this.roundOfficialActivity$.observers.length);
            this.loggingService.debug('ScoringRealtimeService', 'Round official subject is closed:', this.roundOfficialActivity$.closed);
            this.loggingService.debug('ScoringRealtimeService', 'About to call next() on round official Subject...');
            this.roundOfficialActivity$.next(activityData);
            this.loggingService.debug('ScoringRealtimeService', 'Round official activity broadcast sent to Subject successfully');
        } catch (error) {
            this.loggingService.error('ScoringRealtimeService', 'Error broadcasting round official activity:', error);
        }
    }

    /**
     * Store event in service-level history (survives component lifecycle)
     */
    private storeEventInHistory(event: ScorecardActivityData): void {
        // Update total events counter
        const currentTotal = this._totalEventsReceived();
        this._totalEventsReceived.set(currentTotal + 1);

        // Update last event timestamp
        this._lastEventReceived.set(event.timestamp);

        // Add to event history (keep last 1000)
        const currentHistory = this._eventHistory();
        const updatedHistory = [event, ...currentHistory].slice(0, 1000);
        this._eventHistory.set(updatedHistory);

        this.loggingService.debug('ScoringRealtimeService', `Event stored in service history. Total events: ${this._totalEventsReceived()}, History length: ${updatedHistory.length}`);
    }

    // =========================================================================
    // PLAYER GROUP SUBSCRIPTION MANAGEMENT
    // =========================================================================

    /**
     * Subscribe to events for a specific player
     * @param playerId - The ID of the player to subscribe to
     */
    async subscribeToPlayer(playerId: string): Promise<void> {
        try {
            await this.signalRService.joinPlayerGroup(playerId);
            this.loggingService.info('ScoringRealtimeService', `Successfully subscribed to player group: ${playerId}`);
        } catch (error) {
            this.loggingService.error('ScoringRealtimeService', `Failed to subscribe to player ${playerId}:`, error);
            throw error;
        }
    }

    /**
     * Unsubscribe from events for a specific player
     * @param playerId - The ID of the player to unsubscribe from
     */
    async unsubscribeFromPlayer(playerId: string): Promise<void> {
        try {
            await this.signalRService.leavePlayerGroup(playerId);
            this.loggingService.info('ScoringRealtimeService', `Successfully unsubscribed from player group: ${playerId}`);
        } catch (error) {
            this.loggingService.error('ScoringRealtimeService', `Failed to unsubscribe from player ${playerId}:`, error);
            throw error;
        }
    }

    /**
     * Subscribe to events for multiple players (e.g., all players in a group)
     * @param playerIds - Array of player IDs to subscribe to
     */
    async subscribeToPlayers(playerIds: string[]): Promise<void> {
        this.loggingService.info('ScoringRealtimeService', `Subscribing to ${playerIds.length} players: [${playerIds.join(', ')}]`);

        try {
            // Use the unified SubscribeToPlayerEvents method (same as Mobile app)
            await this.signalRService.invoke('SubscribeToPlayerEvents', playerIds);
            this.loggingService.info('ScoringRealtimeService', `Successfully subscribed to ${playerIds.length} player events`);
        } catch (error) {
            this.loggingService.error('ScoringRealtimeService', `Failed to subscribe to players: [${playerIds.join(', ')}]`, error);
            throw error;
        }
    }

    /**
     * Unsubscribe from all player events to receive ALL SignalR events
     * This is useful for admin monitoring pages that need to see all activity
     */
    async unsubscribeFromAllPlayerEvents(): Promise<void> {
        try {
            await this.signalRService.invoke('UnsubscribeFromAllPlayerEvents');
            this.loggingService.info('ScoringRealtimeService', 'Successfully unsubscribed from player-specific mode - now receiving ALL events');
        } catch (error) {
            this.loggingService.error('ScoringRealtimeService', 'Failed to unsubscribe from player events:', error);
            throw error;
        }
    }

    /**
     * Unsubscribe from events for multiple players
     * @param playerIds - Array of player IDs to unsubscribe from
     */
    async unsubscribeFromPlayers(playerIds: string[]): Promise<void> {
        this.loggingService.info('ScoringRealtimeService', `Unsubscribing from ${playerIds.length} players: [${playerIds.join(', ')}]`);

        const unsubscriptionPromises = playerIds.map(async (playerId) => {
            try {
                await this.unsubscribeFromPlayer(playerId);
                return { playerId, success: true };
            } catch (error) {
                this.loggingService.error('ScoringRealtimeService', `Failed to unsubscribe from player ${playerId}:`, error);
                return { playerId, success: false, error };
            }
        });

        const results = await Promise.all(unsubscriptionPromises);
        const successful = results.filter(r => r.success);
        const failed = results.filter(r => !r.success);

        this.loggingService.info('ScoringRealtimeService', `Player unsubscriptions complete: ${successful.length} successful, ${failed.length} failed`);

        if (failed.length > 0) {
            this.loggingService.warn('ScoringRealtimeService', `Failed to unsubscribe from players: [${failed.map(f => f.playerId).join(', ')}]`);
        }
    }

    /**
     * Subscribe to events for all players in a group
     * @param group - The group containing players to subscribe to
     * @param scorecards - Optional scorecards array to extract player IDs from (preferred approach)
     */
    async subscribeToGroupPlayers(group: any, scorecards?: any[]): Promise<void> {
        if (!group) {
            this.loggingService.warn('ScoringRealtimeService', 'Cannot subscribe to group players: group is null/undefined');
            return;
        }

        const playerIds: string[] = [];

        // Primary approach: Extract player IDs from scorecards if provided
        if (scorecards && Array.isArray(scorecards)) {
            for (const scorecard of scorecards) {
                if (scorecard.playerId) {
                    playerIds.push(scorecard.playerId);
                }
            }
            this.loggingService.debug('ScoringRealtimeService', `Found ${playerIds.length} players from scorecards`);
        } else {
            // Fallback approach: Extract player IDs from group structure
            // Extract player IDs from group (handles both individual and team-based groups)
            if (group.players && Array.isArray(group.players)) {
                for (const player of group.players) {
                    if (player.playerId) {
                        playerIds.push(player.playerId);
                    }
                }
            }

            if (group.groupSlots && Array.isArray(group.groupSlots)) {
                for (const slot of group.groupSlots) {
                    // Note: GroupSlot interface may not have direct player property
                    // This is a fallback for legacy group structures
                    if (slot.player?.playerId) {
                        playerIds.push(slot.player.playerId);
                    }
                    if (slot.team?.players && Array.isArray(slot.team.players)) {
                        for (const teamPlayer of slot.team.players) {
                            if (teamPlayer.playerId) {
                                playerIds.push(teamPlayer.playerId);
                            }
                        }
                    }
                }
            }
        }

        // Remove duplicates
        const uniquePlayerIds = [...new Set(playerIds)];

        this.loggingService.info('ScoringRealtimeService', `Subscribing to group ${group.number || group.id} with ${uniquePlayerIds.length} players`);

        if (uniquePlayerIds.length > 0) {
            await this.subscribeToPlayers(uniquePlayerIds);
        } else {
            this.loggingService.warn('ScoringRealtimeService', 'No players found in group for subscription');
        }
    }
}
